import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Users, User, Phone, Mail, CreditCard, MapPin } from 'lucide-react';
import { bookingsAPI } from '../../services/api';
import { translations } from '../../context/LanguageContext';
import toast from 'react-hot-toast';

const LANGS = [
  { code: 'ar', flag: '🇲🇦', label: 'AR' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
];

export default function BookingForm() {
  const { token } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [step, setStep] = useState(1);
  const [lang, setLangState] = useState('ar');

  const setLang = (l) => setLangState(l);
  const isRtl = lang === 'ar';

  const t = (key, arg) => {
    const val = translations[lang]?.[key] ?? translations.en?.[key] ?? key;
    return typeof val === 'function' ? val(arg) : val;
  };

  const [form, setForm] = useState({
    check_in: '',
    check_out: '',
    num_guests: 1,
    notes: '',
    source: 'whatsapp_link',
    guest: {
      full_name: '',
      email: '',
      phone: '',
      nationality: '',
      id_type: 'CIN',
      id_number: '',
    },
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setGuest = (k, v) => setForm(f => ({ ...f, guest: { ...f.guest, [k]: v } }));

  useEffect(() => {
    bookingsAPI.getByToken(token)
      .then(res => setProperty(res.data.data))
      .catch(() => setFetchError(t('bookingForm.linkDesc')))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await bookingsAPI.create({ ...form, property_id: property.id, booking_token: token, total_price: totalPrice || undefined });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nights = form.check_in && form.check_out
    ? Math.max(0, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000))
    : 0;

  const totalPrice = nights && property?.price_per_night
    ? nights * Number(property.price_per_night)
    : null;

  const today = new Date().toISOString().split('T')[0];

  const page = {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
  };

  const langSwitcher = (
    <div dir="ltr" style={{ display:'flex', justifyContent:'flex-end', gap:6, marginBottom:16 }}>
      {LANGS.map(({ code, flag, label }) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          style={{ padding:'5px 10px', background: lang === code ? 'var(--accent)' : 'var(--bg-card)', border: lang === code ? 'none' : '1px solid var(--border)', borderRadius:7, color: lang === code ? 'white' : 'var(--text-muted)', cursor:'pointer', fontSize:12, fontWeight:600 }}
        >
          {flag} {label}
        </button>
      ))}
    </div>
  );

  if (loading) return (
    <div style={{ ...page, justifyContent: 'center' }}>
      <span className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} />
    </div>
  );

  if (fetchError) return (
    <div dir={isRtl ? 'rtl' : 'ltr'} style={{ ...page, justifyContent: 'center', textAlign: 'center' }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        {langSwitcher}
        <div className="card">
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ marginBottom: 8 }}>{t('bookingForm.linkTitle')}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{fetchError}</p>
        </div>
      </div>
    </div>
  );

  if (done) return (
    <div dir={isRtl ? 'rtl' : 'ltr'} style={{ ...page, justifyContent: 'center', textAlign: 'center' }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        {langSwitcher}
        <div className="card">
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" fill="none" stroke="var(--success)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <h2 style={{ marginBottom: 8 }}>{t('bookingForm.doneTitle')}</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {t('bookingForm.doneDesc', property?.name)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} style={page}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        {langSwitcher}

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14, background: 'var(--accent-glow)', border: '1px solid rgba(108,99,255,0.3)', borderRadius: 20, padding: '5px 14px' }}>
            <span style={{ fontSize: 12, color: 'var(--accent-light)', fontWeight: 500 }}>{t('bookingForm.badge')}</span>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{property?.name}</h1>
          {property?.city && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: 'var(--text-muted)', fontSize: 14 }}>
              <MapPin size={13} /> {property.city}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: '12px 18px', marginBottom: 18, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
          {property?.property_type && <span className="badge badge-accent" style={{ fontSize: 11, textTransform: 'capitalize' }}>{property.property_type}</span>}
          {property?.max_guests && <span style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}><Users size={13} /> {t('bookingForm.upToGuests', property.max_guests)}</span>}
          {property?.price_per_night && <span style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>{Number(property.price_per_night).toLocaleString()} MAD / {t('bookingForm.night')}</span>}
        </div>

        <div style={{ display: 'flex', marginBottom: 18 }}>
          {[t('bookingForm.step1'), t('bookingForm.step2')].map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => step > i + 1 && setStep(i + 1)}
              style={{ flex: 1, padding: '10px 8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderBottom: step === i + 1 ? '2px solid var(--accent)' : '2px solid transparent', color: step === i + 1 ? 'var(--accent-light)' : 'var(--text-muted)', cursor: step > i + 1 ? 'pointer' : 'default', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
            >
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: step === i + 1 ? 'var(--accent)' : step > i + 1 ? 'var(--success)' : 'var(--border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white', fontWeight: 700, flexShrink: 0 }}>
                {step > i + 1 ? '✓' : i + 1}
              </span>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card">
            {step === 1 && (
              <>
                <div className="grid-2">
                  <div className="form-group">
                    <label><Calendar size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.checkIn')}</label>
                    <input type="date" value={form.check_in} min={today} onChange={e => set('check_in', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label><Calendar size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.checkOut')}</label>
                    <input type="date" value={form.check_out} min={form.check_in || today} onChange={e => set('check_out', e.target.value)} required />
                  </div>
                </div>
                <div className="form-group">
                  <label><Users size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.numGuests')}</label>
                  <input type="number" min="1" max={property?.max_guests || 20} value={form.num_guests} onChange={e => set('num_guests', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>{t('bookingForm.notes')}</label>
                  <textarea rows={3} placeholder={t('bookingForm.notesPlaceholder')} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
                </div>

                {nights > 0 && (
                  <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{nights} {nights !== 1 ? t('bookingForm.nights') : t('bookingForm.night')}</span>
                      {totalPrice && <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{totalPrice.toLocaleString()} MAD</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                      {new Date(form.check_in).toLocaleDateString('fr-MA')} → {new Date(form.check_out).toLocaleDateString('fr-MA')}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    if (!form.check_in || !form.check_out) { toast.error(t('bookingForm.errorDates')); return; }
                    if (new Date(form.check_out) <= new Date(form.check_in)) { toast.error(t('bookingForm.errorCheckout')); return; }
                    setStep(2);
                  }}
                >
                  {t('bookingForm.continue')}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="form-group">
                  <label><User size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.fullName')}</label>
                  <input placeholder={t('bookingForm.fullNamePlaceholder')} value={form.guest.full_name} onChange={e => setGuest('full_name', e.target.value)} required />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label><Phone size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.phone')}</label>
                    <input placeholder="+212 6XX XXX XXX" value={form.guest.phone} onChange={e => setGuest('phone', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label><Mail size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.email')}</label>
                    <input type="email" placeholder="guest@email.com" value={form.guest.email} onChange={e => setGuest('email', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('bookingForm.nationality')}</label>
                  <input placeholder={t('bookingForm.nationalityDefault')} value={form.guest.nationality} onChange={e => setGuest('nationality', e.target.value)} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label><CreditCard size={12} style={{ display: 'inline', marginRight: 5 }} />{t('bookingForm.idType')}</label>
                    <select value={form.guest.id_type} onChange={e => setGuest('id_type', e.target.value)}>
                      <option value="CIN">{t('bookingForm.cin')}</option>
                      <option value="Passport">{t('bookingForm.passport')}</option>
                      <option value="Residence">{t('bookingForm.residence')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('bookingForm.idNumber')}</label>
                    <input placeholder={t('bookingForm.idNumberPlaceholder')} value={form.guest.id_number} onChange={e => setGuest('id_number', e.target.value)} required />
                  </div>
                </div>

                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, marginTop: 4 }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10, fontWeight: 600 }}>{t('bookingForm.summaryTitle')}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('bookingForm.summaryProperty')}</span>
                    <span style={{ fontWeight: 500 }}>{property?.name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('bookingForm.summaryCheckin')}</span>
                    <span>{new Date(form.check_in).toLocaleDateString('fr-MA')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('bookingForm.summaryCheckout')}</span>
                    <span>{new Date(form.check_out).toLocaleDateString('fr-MA')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: totalPrice ? 10 : 0 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('bookingForm.summaryDuration')}</span>
                    <span>{nights} {nights !== 1 ? t('bookingForm.nights') : t('bookingForm.night')}</span>
                  </div>
                  {totalPrice && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                      <span>{t('bookingForm.summaryTotal')}</span>
                      <span style={{ color: 'var(--gold)' }}>{totalPrice.toLocaleString()} MAD</span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  <button type="button" onClick={() => setStep(1)} className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>{t('bookingForm.back')}</button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={submitting}>
                    {submitting ? <span className="spinner" /> : t('bookingForm.submit')}
                  </button>
                </div>
              </>
            )}
          </div>
        </form>

        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 20 }}>
          {t('bookingForm.footer')}
        </p>
      </div>
    </div>
  );
}
